import { Link } from 'react-router-dom';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const manualSchema = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Lotus Maternal Birth Plan — Digital Guide',
  description: 'Comprehensive birth plan guide with personalized planning tools, postpartum recovery guidance, newborn care essentials, and wellness resources.',
  image: 'https://static.readdy.ai/image/a91ac9e4963865d3d1accc2e5c009ceb/36ebc0060fa1ffaad2945ebbe0c1a44c.png',
  brand: {
    '@type': 'Brand',
    name: 'Lotus Maternal',
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Birth Plan', item: `${siteUrl}/manual` },
    ],
  },
  offers: [
    {
      '@type': 'Offer',
      price: '10.00',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      name: 'Birth Plan Only',
      url: `${siteUrl}/manual`,
      seller: { '@type': 'MedicalBusiness', name: 'Lotus Maternal', url: siteUrl },
    },
    {
      '@type': 'Offer',
      price: '40.00',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      name: 'Birth Plan + Customized Chart',
      url: `${siteUrl}/manual`,
      seller: { '@type': 'MedicalBusiness', name: 'Lotus Maternal', url: siteUrl },
    },
  ],
};

export default function ManualPage() {
  const tiers = [
    {
      id: 'basic' as const,
      name: 'Birth Plan',
      price: 10,
      description: 'Complete birth plan guide with essential resources',
      features: [
        'Birth planning worksheets',
        'Postpartum recovery guide',
        'Newborn care essentials',
        'Self-care practices',
        'Resource directory',
        'Instant PDF download',
      ],
    },
    {
      id: 'premium' as const,
      name: 'Birth Plan + Customized Chart',
      price: 40,
      description: 'Everything in Birth Plan plus personalized consultancy',
      features: [
        'Everything in Birth Plan',
        'Personalized birth preferences chart',
        'Custom postpartum plan',
        'One-on-one chart review session',
        'Tailored to your unique journey',
        'Email support for questions',
      ],
      badge: 'Most Popular',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-green-50">
      <SeoHead
        title="Birth Plan Digital Guide — Download | Lotus Maternal"
        description="Download the Lotus Maternal Birth Plan guide. Choose the complete guide ($10) or add a customized birth chart consultation ($40). Included free with all doula packages."
        keywords="birth plan guide download, birth planning worksheet, postpartum guide, doula resources, birth chart consultation, pregnancy planning guide"
        canonical={`${siteUrl}/manual`}
        ogTitle="Birth Plan Digital Guide | Lotus Maternal"
        ogDescription="Comprehensive birth plan guide with personalized planning tools and postpartum recovery resources. Starting at $10 — free with any doula package."
        schema={manualSchema}
      />

      {/* Hero */}
      <div className="relative h-72 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/a16bb92e866c2275229d73438e8a6df9.jpg"
          alt="Birth Plan"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-transparent"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h1 className="font-display text-5xl lg:text-6xl font-bold text-white drop-shadow-lg mb-3">
            Birth Plan
          </h1>
          <p className="text-white/90 text-lg max-w-2xl drop-shadow-md">
            Your comprehensive guide to pregnancy, birth, and postpartum wellness
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Free with Package Notice */}
        <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl p-6 mb-12 border border-green-200">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-600 text-white flex-shrink-0">
              <i className="ri-gift-line text-2xl"></i>
            </div>
            <div>
              <h3 className="font-display text-xl font-bold text-neutral-800 mb-2">
                Included Free with Any Package Purchase
              </h3>
              <p className="text-neutral-700 text-sm leading-relaxed">
                When you book any of our doula care packages (Princess, Queen, Empress,
                Goddess, Prenatal, Postpartum, or Full Journey), you'll receive the{' '}
                <strong>Birth Plan + Customized Chart</strong> at no additional cost — a $40 value! This
                is our gift to support your journey.
              </p>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-200 to-green-200 rounded-3xl blur-2xl opacity-30"></div>
              <img
                src="https://static.readdy.ai/image/a91ac9e4963865d3d1accc2e5c009ceb/36ebc0060fa1ffaad2945ebbe0c1a44c.png"
                alt="Birth Plan Cover"
                className="relative w-full max-w-md rounded-2xl shadow-2xl"
              />
            </div>
          </div>
          <div className="flex flex-col justify-center">
            <h2 className="font-display text-4xl font-bold text-neutral-800 mb-6">
              Everything You Need for Your Journey
            </h2>
            <p className="text-neutral-600 leading-relaxed mb-6">
              The Lotus Maternal Birth Plan is a comprehensive resource designed to empower
              you through pregnancy, birth, and postpartum. Whether you're planning your birth
              preferences, preparing for recovery, or navigating newborn care, this guide has
              you covered.
            </p>
            <div className="space-y-4">
              {[
                { title: 'Birth Planning Tools', desc: 'Worksheets to clarify your preferences and communicate with your care team' },
                { title: 'Postpartum Recovery', desc: 'Evidence-based guidance for healing and self-care' },
                { title: 'Newborn Essentials', desc: 'Feeding, sleep, and care basics for the first weeks' },
                { title: 'Resource Directory', desc: 'Trusted contacts, hotlines, and community support' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 flex-shrink-0">
                    <i className="ri-check-line text-sm"></i>
                  </div>
                  <p className="text-neutral-700 text-sm">
                    <strong>{item.title}:</strong> {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pricing Tiers */}
        <div className="mb-16">
          <h2 className="font-display text-4xl font-bold text-neutral-800 text-center mb-4">
            Choose Your Option
          </h2>
          <p className="text-neutral-600 text-center mb-12 max-w-2xl mx-auto">
            Select the option that best fits your needs. Both include instant digital download.
          </p>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className={`relative bg-white rounded-3xl shadow-xl p-8 border-2 transition-all hover:shadow-2xl hover:scale-105 ${
                  tier.badge ? 'border-amber-500' : 'border-amber-100'
                }`}
              >
                {tier.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-block px-6 py-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white text-xs font-bold rounded-full shadow-lg whitespace-nowrap">
                      {tier.badge}
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-display text-2xl font-bold text-neutral-800 mb-2">
                    {tier.name}
                  </h3>
                  <p className="text-neutral-600 text-sm mb-4">{tier.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-display font-bold text-amber-700">
                      ${tier.price}
                    </span>
                    <span className="text-neutral-500 text-sm">one-time</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-green-100 text-green-600 flex-shrink-0">
                        <i className="ri-check-line text-xs"></i>
                      </div>
                      <span className="text-neutral-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/checkout?item=${encodeURIComponent(tier.name)}&price=${tier.price}&type=manual`}
                  className={`block w-full py-4 font-bold rounded-full transition-all whitespace-nowrap cursor-pointer text-center ${
                    tier.badge
                      ? 'bg-gradient-to-r from-amber-700 to-amber-600 text-white hover:shadow-xl hover:scale-105'
                      : 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200'
                  }`}
                >
                  Purchase {tier.name}
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h3 className="font-display text-3xl font-bold text-neutral-800 text-center mb-8">
            Frequently Asked Questions
          </h3>
          <div className="space-y-4">
            {[
              {
                q: "What's the difference between the two options?",
                a: "The Birth Plan option gives you the complete digital guide with all resources. The Birth Plan + Customized Chart option includes everything in the birth plan plus a personalized birth preferences chart and a one-on-one review session with our team to tailor it to your unique needs.",
              },
              {
                q: 'How do I receive the birth plan after purchase?',
                a: "Immediately after your payment is confirmed, you'll see a download button on this page. You'll also receive an email with a download link to access your birth plan anytime.",
              },
              {
                q: 'Is the birth plan included if I book a package?',
                a: 'Yes! When you book any of our doula care packages, you automatically receive the Birth Plan + Customized Chart (a $40 value) at no extra cost. You don\'t need to purchase it separately.',
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major credit and debit cards, Apple Pay, Google Pay, and Cash App Pay through our secure Stripe payment system.',
              },
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-md border border-amber-100">
                <h4 className="font-semibold text-neutral-800 mb-2">{faq.q}</h4>
                <p className="text-neutral-600 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
